import { Alert } from '@chakra-ui/react';
import React from 'react';

import type { SmartContractProxyType } from 'types/api/contract';

import { useI18n } from 'lib/i18n/context';
import LinkExternal from 'ui/shared/links/LinkExternal';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';

interface Props {
  type: NonNullable<SmartContractProxyType>;
}

const PROXY_TYPES: Record<NonNullable<SmartContractProxyType>, {
  name: string;
  link?: string;
  descriptionKey?: string;
}> = {
  eip1167: {
    name: 'EIP-1167',
    link: 'https://eips.ethereum.org/EIPS/eip-1167',
    descriptionKey: ADDRESS_CONTRACT_KEYS.proxy.minimalProxy,
  },
  eip1967: {
    name: 'EIP-1967',
    link: 'https://eips.ethereum.org/EIPS/eip-1967',
    descriptionKey: ADDRESS_CONTRACT_KEYS.proxy.proxyStorageSlots,
  },
  eip1822: {
    name: 'EIP-1822',
    link: 'https://eips.ethereum.org/EIPS/eip-1822',
    descriptionKey: ADDRESS_CONTRACT_KEYS.proxy.universalUpgradeableProxy,
  },
  eip2535: {
    name: 'EIP-2535',
    link: 'https://eips.ethereum.org/EIPS/eip-2535',
    descriptionKey: ADDRESS_CONTRACT_KEYS.proxy.diamondProxy,
  },
  eip930: {
    name: 'ERC-930',
    link: 'https://github.com/ethereum/EIPs/issues/930',
    descriptionKey: ADDRESS_CONTRACT_KEYS.proxy.eternalStorage,
  },
  clone_with_immutable_arguments: {
    name: 'Clones with immutable arguments',
    link: 'https://github.com/wighawag/clones-with-immutable-args',
  },
  master_copy: {
    name: 'Safe proxy',
    link: 'https://github.com/safe-global/safe-smart-account',
  },
  comptroller: {
    name: 'Compound protocol proxy',
    link: 'https://github.com/compound-finance/compound-protocol',
  },
  basic_implementation: {
    name: 'public implementation() getter',
  },
  basic_get_implementation: {
    name: 'public getImplementation() getter',
  },
  unknown: {
    name: 'Unknown proxy pattern',
  },
};

const ContractCodeProxyPattern = ({ type }: Props) => {
  const { t } = useI18n();
  const proxyInfo = PROXY_TYPES[type];

  if (!proxyInfo || type === 'unknown') {
    return null;
  }

  const description = proxyInfo.descriptionKey ? t(proxyInfo.descriptionKey) : undefined;

  return (
    <Alert status="warning" flexWrap="wrap" whiteSpace="pre-wrap" borderRadius="8px">
      { proxyInfo.link ? (
        <>
          { t(ADDRESS_CONTRACT_KEYS.notice.proxyDetectedPrefix) } <LinkExternal href={ proxyInfo.link }>{ proxyInfo.name }</LinkExternal>
          { t(ADDRESS_CONTRACT_KEYS.notice.proxyDetectedSuffix) }
          { description && ` - ${ description }` }
        </>
      ) : (
        <>
          { t(ADDRESS_CONTRACT_KEYS.notice.proxyDetectedVia, { name: proxyInfo.name }) }
          { description && ` - ${ description }` }
        </>
      ) }
    </Alert>
  );
};

export default React.memo(ContractCodeProxyPattern);
